import React from "react";
import { connect } from "react-redux";
import { compose, withHandlers, withProps } from "recompose";
import { Field, reduxForm } from "redux-form";
import { Grid, Typography, Divider, Box, MenuItem } from "@material-ui/core";
import InputWithTitle from "../../../../../components/InputWithTitle/InputWithTitle";
import SelectWithTitleStateFull from "../../../../../components/SelectWithTitle/SelectWithTitleStateFull";
import SelectItem from "../../../../../components/SelectItem/SelectItem";
import ConfirmButton from "../../../../../components/ConfirmButton/ConfirmButton";
import AlertBox from "../../../../../components/AlertBox/AlertBox";
import PropTypes from "prop-types";
import {
  TEST_WEBHOOK,
  SIMULATE_TYPE_WEBHOOKS,
  DETAILS_FOR_TESTING,
  TO_SEND_TEST_MESSAGE,
  SELECT_WEBHOOK,
  EMAIL,
  SMS,
  TEST_URL,
  SECRET,
  YOUR_SECRET,
  PLEASE_CREATE_SECRET_TO_SEND_TEST,
  WEBHOOK_SNACKBAR_MESSAGES,
  WEBHOOK_TYPES,
  TEST_EVENT_CODE,
} from "../../../../../constants/strings";
import { isEmailValid } from "../../../../../utils/commonValidation";
import { PHONE_NUMBER_REGEX } from "../../../../../constants/regex";
import {
  TEST_NOTIFICATIONS_FORM,
  TEST_EVENTS_FORM,
} from "../../../../../constants/forms";
import { validate, validateTestEventsForm } from "./TestWebhook.validate";
import styles from "./TestNotifications.module.scss";
import { testWebhookAction } from "../../../../../features/webhooks/webhook.actions";
import { showSnackbar } from "../../../../../features/snackbar/snackbar.actions";

const isAllGroupKey = (key) => key?.toString().toUpperCase() === "ALL";

const getTestEventCode = (selectedValue) => {
  const category = selectedValue.split("::")[0];
  return TEST_EVENT_CODE[category] ?? "";
};

const SelectWithTitleStateFullField = ({ input, meta, ...props }) => (
  <SelectWithTitleStateFull
    {...props}
    value={input.value || ""}
    onChange={input.onChange}
    onBlur={input.onBlur}
  />
);

const TestWebhook = ({
  webhookType,
  dirtyConfig = {},
  config = {},
  handleSubmit,
  onSubmitPress,
  valid,
  values = {},
  formValues = {},
  formValid = false,
}) => {
  const isEventType = webhookType === WEBHOOK_TYPES.EVENT;

  let toggleData = {};
  switch (webhookType) {
    case WEBHOOK_TYPES.EVENT:
      toggleData = dirtyConfig?.events || {};
      break;
    default:
      toggleData = dirtyConfig?.notifications || {};
      break;
  }

  const toggleOptions = Object.entries(toggleData).reduce(
    (acc, [category, items]) => {
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (item && typeof item === "object") {
            const group = item.group || "";
            if (isEventType && isAllGroupKey(group)) {
              return;
            }
            const value = isEventType
              ? `${category}::${group}`
              : item.code || "";
            acc.push({
              label: isEventType
                ? item.description || item.group || ""
                : item.name || item.code || "",
              value,
            });
          }
        });
      }
      return acc;
    },
    []
  );

  const inUseSecret = config?.secrets?.find(
    (secret) => secret.inUse
  )?.sharedSecret;

  const currentValues =
    Object.keys(formValues).length > 0 ? formValues : values;

  const hasSelectedNotification = currentValues.selectedNotification?.trim();
  const hasTestUrl = currentValues.testUrl?.trim();
  const hasEmail = currentValues.email?.trim();
  const hasSms = currentValues.sms?.trim();

  const isValidEmail = hasEmail && isEmailValid(hasEmail);
  const isValidSms = hasSms && PHONE_NUMBER_REGEX.test(hasSms);
  const hasValidEmailOrSms = isValidEmail || isValidSms;

  const isFormValid = isEventType
    ? hasSelectedNotification && hasTestUrl && (valid || formValid)
    : hasSelectedNotification &&
      hasTestUrl &&
      hasValidEmailOrSms &&
      (valid || formValid) &&
      !!inUseSecret;

  return (
    <>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">{TEST_WEBHOOK(webhookType)}</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {SIMULATE_TYPE_WEBHOOKS(webhookType)}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Box
          style={{
            padding: 24,
            gap: 8,
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography variant="body1" gutterBottom>
            {DETAILS_FOR_TESTING}
          </Typography>
          {!isEventType && (
            <Typography
              variant="body2"
              color="textSecondary"
              gutterBottom
              style={{ marginBottom: "24px" }}
            >
              {TO_SEND_TEST_MESSAGE}
            </Typography>
          )}

          <form onSubmit={handleSubmit(onSubmitPress)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Field
                  name="selectedNotification"
                  component={SelectWithTitleStateFullField}
                  displayEmpty
                  className={styles.dropdown}
                  containerStyles={styles.dropdownContainer}
                  style={{ backgroundColor: "white" }}
                  renderValue={(value) =>
                    (value &&
                      toggleOptions.find((opt) => opt.value === value)
                        ?.label) ||
                    SELECT_WEBHOOK(webhookType)
                  }
                >
                  {toggleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <SelectItem
                        title={option.label}
                        containerStyle={styles.menuItem}
                      />
                    </MenuItem>
                  ))}
                </Field>
              </Grid>

              <Grid item xs={12}>
                <Field
                  name="testUrl"
                  component={InputWithTitle}
                  placeholder={TEST_URL}
                  style={{ backgroundColor: "white" }}
                />
              </Grid>

              {webhookType === WEBHOOK_TYPES.NOTIFICATION && (
                <>
                  <Grid item xs={12}>
                    <Field
                      name="email"
                      component={InputWithTitle}
                      placeholder={EMAIL}
                      style={{ backgroundColor: "white" }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      name="sms"
                      component={InputWithTitle}
                      placeholder={SMS}
                      style={{ backgroundColor: "white" }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <AlertBox
                  variant={inUseSecret ? "info" : "warning"}
                  title={SECRET}
                  subtitle={
                    inUseSecret
                      ? `${YOUR_SECRET}: ${inUseSecret}`
                      : `${PLEASE_CREATE_SECRET_TO_SEND_TEST}`
                  }
                ></AlertBox>
              </Grid>

              <Grid item xs={12}>
                <Box
                  style={{
                    marginTop: "16px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <ConfirmButton
                    type="submit"
                    text="Send Test"
                    disabled={!isFormValid}
                    style={{
                      minWidth: "121px",
                      height: "43px",
                      padding: "16px 12px",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
    </>
  );
};

TestWebhook.propTypes = {
  dirtyConfig: PropTypes.object,
  config: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  onSubmitPress: PropTypes.func.isRequired,
  valid: PropTypes.bool,
  values: PropTypes.object,
  formValues: PropTypes.object,
  formValid: PropTypes.bool,
  accountNumber: PropTypes.string,
  webhookType: PropTypes.string,
  businessUnit: PropTypes.number,
};

const getTestFormName = (webhookType) =>
  webhookType === WEBHOOK_TYPES.EVENT
    ? TEST_EVENTS_FORM
    : TEST_NOTIFICATIONS_FORM;

const mapStateToProps = (state, ownProps) => {
  const formName = getTestFormName(ownProps.webhookType);
  const formState = state.form && state.form[formName];
  return {
    formValues: formState ? formState.values : {},
    formValid: formState ? formState.syncErrors === undefined : false,
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

const buildTestWebhookRequestBody = (webhookType, formValues) => {
  const url = formValues.testUrl;
  switch (webhookType) {
    case WEBHOOK_TYPES.EVENT:
      return {
        url,
        eventCode: getTestEventCode(formValues.selectedNotification),
      };
    case WEBHOOK_TYPES.NOTIFICATION:
    default:
      return {
        url,
        email: formValues.email,
        sms: formValues.sms,
        notifyType: formValues.selectedNotification,
      };
  }
};

const onSubmitPress =
  ({ dispatch, accountNumber, webhookType, businessUnit }) =>
  async (formValues) => {
    if (!accountNumber || !webhookType) {
      dispatch(
        showSnackbar({
          message: WEBHOOK_SNACKBAR_MESSAGES.ACCOUNT_NUMBER_AND_TYPE_REQUIRED,
          variant: "error",
        })
      );
      return;
    }

    try {
      const requestBody = buildTestWebhookRequestBody(webhookType, formValues);

      const response = await dispatch(
        testWebhookAction(accountNumber, webhookType, businessUnit, requestBody)
      );
      if (
        response?.success === false ||
        (response?.status && response.status >= 400)
      ) {
        dispatch(
          showSnackbar({
            message: WEBHOOK_SNACKBAR_MESSAGES.TEST_SEND_FAILED,
            variant: "error",
          })
        );
      } else {
        dispatch(
          showSnackbar({
            message: WEBHOOK_SNACKBAR_MESSAGES.TEST_SENT_SUCCESS,
            variant: "success",
          })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: WEBHOOK_SNACKBAR_MESSAGES.TEST_SEND_FAILED,
          variant: "error",
        })
      );
    }
  };

const validateByWebhookType = (values, props) =>
  props.webhookType === WEBHOOK_TYPES.EVENT
    ? validateTestEventsForm(values)
    : validate(values);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({ config, webhookType }) => ({
    initialValues: {
      testUrl: config?.url || "",
    },
    form: getTestFormName(webhookType),
  })),
  reduxForm({
    form: TEST_NOTIFICATIONS_FORM,
    validate: validateByWebhookType,
    enableReinitialize: true,
  }),
  withHandlers({
    onSubmitPress: ({ dispatch, accountNumber, webhookType, businessUnit }) =>
      onSubmitPress({ dispatch, accountNumber, webhookType, businessUnit }),
  })
)(TestWebhook);
